import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

// Usage:
// <element {{animate-height opened=this.isExpanded}}>
// <element {{animate-height update=this.contentUpdate}}>
// Optional named args:
// - duration: animation duration in ms, default 200
// - easing: CSS easing function, default 'ease-in-out'
// - opened: boolean to toggle open/closed state
// - update: any value that when changed triggers height recalculation
export default class AnimateHeightModifier extends Modifier {
  element = null;
  opened = false;
  update = null;
  duration = 200;
  easing = 'ease-in-out';
  _timeoutId = null;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => this.teardown());
  }

  modify(element, positionalArgs = [], namedArgs = {}) {
    this.element = element;
    this.opened = namedArgs.opened ?? false;
    this.update = namedArgs.update;
    this.duration = namedArgs.duration ?? 200;
    this.targetHeight = namedArgs.height ?? null;
    this.easing = namedArgs.easing ?? 'ease-in-out';

    // Set up CSS transition
    if (!this.isInitialized) {
      this.setupTransition();
      this.isInitialized = true;
    }

    // Handle state changes
    this.handleStateChange();
  }

  setupTransition() {
    if (!this.element) return;

    // Set up CSS transition properties
    this.element.style.overflow = 'hidden';
    this.element.style.height = '0px';
    this.element.style.minHeight = '0px';
    this.element.style.opacity = '0';
    this.element.style.transition = `
      height ${this.duration}ms ${this.easing},
      margin ${this.duration}ms ${this.easing},
      padding ${this.duration}ms ${this.easing},
      opacity ${this.duration}ms ${this.easing}`;
  }

  handleStateChange() {
    if (!this.element) return;

    if (this.opened) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  expand() {
    if (!this.element) return;

    this.clearTimeout();

    // Get the natural height of the content
    const naturalHeight = this.targetHeight ?? this.element.scrollHeight;

    // Set the height to the natural height for smooth transition
    this.element.style.height = `${naturalHeight}px`;
    this.element.style.opacity = '1';
    this.element.style.overflow = '';
    this.element.style.margin = '';
    this.element.style.padding = '';

    // After transition completes, set to auto to handle dynamic content
    this._timeoutId = setTimeout(() => {
      this.element.style.height = 'auto';
      this.element.style.minHeight = '';
      this._timeoutId = null;
    }, this.duration);
  }

  collapse() {
    if (!this.element) return;

    // clear expand timeout in case of fast double click
    this.clearTimeout();

    // For collapsing, we need to set a specific height first
    const currentHeight = this.element.scrollHeight;
    this.element.style.height = `${currentHeight}px`;
    this.element.style.minHeight = '0px';

    // Force reflow
    this.element.offsetHeight;

    // Then animate to 0
    this.element.style.height = '0px';
    this.element.style.opacity = '0';
    this.element.style.margin = '0px';
    this.element.style.padding = '0px';
  }

  clearTimeout() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  }

  teardown() {
    this.clearTimeout();
    if (this.element) {
      // Reset styles
      this.element.style.height = '';
      this.element.style.overflow = '';
      this.element.style.opacity = '';
      this.element.style.margin = '';
      this.element.style.padding = '';
    }
  }
}
